import { useEffect } from 'react';
import { createRootRouteWithContext, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { useT } from '@mosaferati/i18n';
import { OfflineBanner } from '@mosaferati/ui';
import { registerServiceWorker } from '@/lib/sw-register.ts';
import { installIOSViewportFix } from '@/lib/ios-viewport.ts';
import { installViewportDebug } from '@/lib/viewport-debug.ts';
import { InstallAndUpdate } from '@/features/pwa/InstallAndUpdate.tsx';
import { usePrefs } from '@/app/prefs-store.ts';

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
});

function RootLayout() {
  const t = useT();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const onboarded = usePrefs((s) => s.onboarded);
  useEffect(() => {
    registerServiceWorker((url) => void navigate({ to: url as '/' }));
  }, [navigate]);
  useEffect(() => installIOSViewportFix(), []);
  useEffect(() => installViewportDebug(), []);
  // route changes unmount focused inputs without a blur on iOS — re-pin the window after each navigation
  useEffect(() => {
    if (window.scrollY !== 0) window.scrollTo(0, 0);
    document.querySelectorAll<HTMLElement>('.m-app-shell').forEach((e) => { e.scrollTop = 0; });
  }, [pathname]);
  // The install banner only appears on Today (never over flows or long settings lists).
  const engaged = onboarded && pathname === '/';
  return (
    <div className="m-app-shell">
      <OfflineBanner message={t('common.offline')} />
      <Outlet />
      <InstallAndUpdate engaged={engaged} />
    </div>
  );
}

function NotFound() {
  const t = useT();
  return (
    <div className="flex h-full items-center justify-center p-8 text-center text-text-secondary">
      <p>{t('common.notFound')}</p>
    </div>
  );
}
